// Admin API client. Uses Bearer JWT in localStorage.
const TOKEN_KEY = "ridgehawk_admin_token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const tokenStore = {
  get: () => {
    try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
  },
  set: (t) => {
    try { localStorage.setItem(TOKEN_KEY, t); } catch (_) {}
  },
  clear: () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch (_) {}
  },
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" • ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

async function request(path, { method = "GET", body, auth = false, isForm = false } = {}) {
  const headers = {};
  if (auth) {
    const t = tokenStore.get();
    if (t) headers["Authorization"] = `Bearer ${t}`;
  }
  let payload = body;
  if (body !== undefined && !isForm) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(`${API}${path}`, { method, headers, body: payload });
  } catch (_) {
    return { ok: false, status: 0, error: "Network error" };
  }
  if (res.status === 401 && auth) {
    tokenStore.clear();
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin/login")) {
      window.location.assign("/admin/login");
    }
    return { ok: false, status: 401, error: "Unauthorized" };
  }
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      error: formatApiErrorDetail(data?.detail) || `Request failed (${res.status})`,
      raw: data,
    };
  }
  return { ok: true, status: res.status, data };
}

export const adminApi = {
  // Auth
  login: (email, password) => request("/auth/login", { method: "POST", body: { email, password } }),
  me: () => request("/auth/me", { auth: true }),
  logout: () => request("/auth/logout", { method: "POST", auth: true }),
  changePassword: (current_password, new_password) =>
    request("/auth/change-password", { method: "POST", auth: true, body: { current_password, new_password } }),

  // Leads (admin)
  listLeads: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
    return request(`/admin/leads${qs ? `?${qs}` : ""}`, { auth: true });
  },
  getLead: (id) => request(`/admin/leads/${id}`, { auth: true }),
  patchLead: (id, body) => request(`/admin/leads/${id}`, { method: "PATCH", auth: true, body }),
  leadStats: () => request(`/admin/leads/stats`, { auth: true }),
  exportCsvUrl: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
    return `${API}/admin/leads/export.csv${qs ? `?${qs}` : ""}`;
  },

  // Blog
  listBlog: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
    return request(`/admin/blog${qs ? `?${qs}` : ""}`, { auth: true });
  },
  getBlog: (id) => request(`/admin/blog/${id}`, { auth: true }),
  createBlog: (body) => request(`/admin/blog`, { method: "POST", auth: true, body }),
  updateBlog: (id, body) => request(`/admin/blog/${id}`, { method: "PATCH", auth: true, body }),
  deleteBlog: (id) => request(`/admin/blog/${id}`, { method: "DELETE", auth: true }),

  // Case studies
  listCases: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
    return request(`/admin/case-studies${qs ? `?${qs}` : ""}`, { auth: true });
  },
  getCase: (id) => request(`/admin/case-studies/${id}`, { auth: true }),
  createCase: (body) => request(`/admin/case-studies`, { method: "POST", auth: true, body }),
  updateCase: (id, body) => request(`/admin/case-studies/${id}`, { method: "PATCH", auth: true, body }),
  deleteCase: (id) => request(`/admin/case-studies/${id}`, { method: "DELETE", auth: true }),

  // Image upload
  uploadImage: async (file) => {
    const t = tokenStore.get();
    const fd = new FormData();
    fd.append("file", file);
    let res;
    try {
      res = await fetch(`${API}/admin/upload-image`, {
        method: "POST",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        body: fd,
      });
    } catch (_) {
      return { ok: false, error: "Network error" };
    }
    let data = null;
    try { data = await res.json(); } catch (_) {}
    if (!res.ok) return { ok: false, error: formatApiErrorDetail(data?.detail) || `Upload failed (${res.status})` };
    return { ok: true, data };
  },
};

export const publicApi = {
  listBlog: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
    return request(`/blog${qs ? `?${qs}` : ""}`);
  },
  getBlog: (slug) => request(`/blog/${slug}`),
  listCases: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
    return request(`/case-studies${qs ? `?${qs}` : ""}`);
  },
  getCase: (slug) => request(`/case-studies/${slug}`),
};
